import { isEmpty } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Joyride, {
  ACTIONS,
  type CallBackProps,
  EVENTS,
  STATUS,
  type Step,
} from 'react-joyride';
import { useNavigate } from 'react-router';
import { useCookie } from 'react-use';
import { useMutation } from 'urql';

import {
  selectImpersonationOverrides,
  useGetLoggedInUser,
  useImpersonationSelector,
} from '@revelio/auth';
import {
  FeatureFlag,
  InitiationTrackingEvents,
  InterestedOptions,
  PagePaths,
  TourCustomPageValue,
  useTourStore,
} from '@revelio/core';
import {
  type KeyValueResp,
  MetadataKey,
  type User,
} from '@revelio/data-access';

import { useBrand } from '../../shared/branding/brand-provider';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { UpdateUserMutation } from '../adminRewrite/userOperations';
import {
  getTourStepPhase,
  TourStepPhase,
  steps as tourSteps,
} from './tour.config';
import { tourStyles } from './tour.styles';
import { isElementVisible, pollUntil } from './tour.utils';

const TAB_CLICK_DELAY_MS = 100;
const TAB_SETTLE_DELAY_MS = 50;

export function Tour() {
  const navigate = useNavigate();
  const snapshotIndex = 0;
  const overtimeIndex = 1;

  const [, updateUser] = useMutation(UpdateUserMutation);

  const impersonationOverrides = useImpersonationSelector(
    selectImpersonationOverrides
  );
  const [tourCustomPageValue] = useCookie(TourCustomPageValue);
  const cookieInterestedInReports = tourCustomPageValue?.includes(
    InterestedOptions.Data_Enrichment_and_Custom_Reports
  );
  const cookieInterestedInTalent = tourCustomPageValue?.includes(
    InterestedOptions.Talent_Acquisition
  );
  const interestedInReports = Boolean(
    impersonationOverrides?.tourInterestCustomData ?? cookieInterestedInReports
  );
  const interestedInTalent = Boolean(
    impersonationOverrides?.tourInterestTalentAcquisition ??
      cookieInterestedInTalent
  );
  const interestedInSpecials = interestedInReports || interestedInTalent;
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);

  const { clientName, displayName } = useBrand();
  const isWhitelabel = clientName !== 'default';

  const steps = useMemo(
    () =>
      tourSteps.reduce<Step[]>((filteredSteps, rawStep) => {
        if (rawStep.newDashboardLayoutOnly && !isNewDashboardLayout) {
          return filteredSteps;
        }

        const phaseEarly = getTourStepPhase(rawStep);

        if (phaseEarly === TourStepPhase.CustomData && !interestedInReports) {
          return filteredSteps;
        }

        if (
          phaseEarly === TourStepPhase.TalentAcquisition &&
          !interestedInTalent
        ) {
          return filteredSteps;
        }

        const { legacyContent, legacyTitle, newDashboardLayoutOnly, ...rest } =
          rawStep;
        void newDashboardLayoutOnly;

        const nextStep: Step = { ...rest };
        const phase = phaseEarly;

        if (phase === TourStepPhase.AdjustView) {
          nextStep.showProgress = interestedInSpecials;
        }

        if (phase === TourStepPhase.CustomData) {
          nextStep.showProgress = interestedInTalent;
        }

        if (!isNewDashboardLayout && legacyContent !== undefined) {
          nextStep.content = legacyContent;
        }

        if (
          !isNewDashboardLayout &&
          legacyTitle !== undefined &&
          legacyTitle !== ''
        ) {
          nextStep.title = legacyTitle;
        }

        if (phase === TourStepPhase.Welcome) {
          nextStep.title = `Welcome to ${displayName}`;
        }

        return [...filteredSteps, nextStep];
      }, []),
    [
      interestedInSpecials,
      interestedInTalent,
      interestedInReports,
      isNewDashboardLayout,
      displayName,
    ]
  );

  const pageTabsStepIndex = useMemo(
    () =>
      steps.findIndex((s) => getTourStepPhase(s) === TourStepPhase.PageTabs),
    [steps]
  );

  const [stepIndexState, setStepIndexState] = useState(0);
  /**
   * Joyride does not reliably re-render its tooltip when `stepIndex` is set
   * to the same value it already holds (e.g. during back-navigation to the
   * current step). Bumping this key forces a full Joyride remount, which
   * guarantees the tooltip repositions to the correct target element.
   * See: https://github.com/gilbarbara/react-joyride/issues/641
   */
  const [tourRenderKey, setTourRenderKey] = useState(0);
  const [targetOverrides, setTargetOverrides] = useState<
    Record<number, string>
  >({});
  const stepIndexRef = useRef(0);
  /**
   * Guard flag to prevent spurious Joyride callbacks during programmatic step
   * transitions. When we set stepIndex (e.g., during back navigation), Joyride
   * can fire extra step:after events that don't correspond to actual user actions,
   * creating an infinite loop between steps. This flag is set when we begin
   * processing a step:after event and cleared when Joyride renders the tooltip
   * for the new step (indicating the transition is complete).
   */
  const isProcessingStepChangeRef = useRef(false);
  const cancelPollingRef = useRef<(() => void) | null>(null);
  /**
   * New layout: leaving Analytics (subnav spotlight) defers closing the flyout
   * until Joyride has advanced to Page tabs so the spotlight clears first.
   */
  const deferSidebarCloseUntilPageTabsStepRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelPollingRef.current?.();
      cancelPollingRef.current = null;
    };
  }, []);

  const { loggedInUser, refetch: refetchUserData } = useGetLoggedInUser();

  const isTourState = useTourStore((s) => s.isTour);
  const isTourComplete = useTourStore((s) => s.isTourComplete);
  const setIsTour = useTourStore((s) => s.setIsTour);
  const setStepIndex = useTourStore((s) => s.setStepIndex);
  const setIsTourComplete = useTourStore((s) => s.setIsTourComplete);
  const setIsSideBarOpen = useTourStore((s) => s.setIsSideBarOpen);
  const setIsSideBarCompany = useTourStore((s) => s.setIsSideBarCompany);
  const setIsControlPanelSpotlighted = useTourStore(
    (s) => s.setIsControlPanelSpotlighted
  );
  // Auto-start the tour for whitelabel users on first login. Non-whitelabel
  // users are prompted via the Welcome modal in private-route.tsx instead.
  // `completeTour` writes `tour_completed` metadata on skip/finish, so this
  // won't re-fire on subsequent logins.
  useEffect(() => {
    if (!isWhitelabel) return;
    if (isEmpty(loggedInUser)) return;
    if (isTourState || isTourComplete) return;

    const tourCompletedMeta = (loggedInUser.metadata || []).find(
      (m: KeyValueResp | null) => m?.key === MetadataKey.TourCompleted
    );
    if (tourCompletedMeta?.value === 'true') return;

    setIsTour(true);
  }, [isWhitelabel, loggedInUser, isTourState, isTourComplete, setIsTour]);

  useEffect(() => {
    if (!deferSidebarCloseUntilPageTabsStepRef.current) {
      return;
    }
    if (!isTourState) {
      return;
    }
    if (pageTabsStepIndex < 0) {
      deferSidebarCloseUntilPageTabsStepRef.current = false;
      setIsSideBarOpen(false);
      return;
    }
    if (stepIndexState !== pageTabsStepIndex) {
      return;
    }
    deferSidebarCloseUntilPageTabsStepRef.current = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsSideBarOpen(false);
      });
    });
  }, [isTourState, pageTabsStepIndex, setIsSideBarOpen, stepIndexState]);

  const switchTab = (index: number): Promise<void> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const tabButtons = document.querySelectorAll('[role="tab"]');
        if (tabButtons.length > index) {
          (tabButtons[index] as HTMLElement).click();
        }
        setTimeout(resolve, TAB_SETTLE_DELAY_MS);
      }, TAB_CLICK_DELAY_MS);
    });
  };

  const handleUpdateMetadata = (
    loggedInUser: User = {},
    newMeta: KeyValueResp[]
  ) => {
    const metaFieldsToExclude = [MetadataKey.ExpirationDate];

    return new Promise((resolve, reject) => {
      if (isEmpty(loggedInUser)) {
        reject('No logged in user provided');
      }

      const newMetaKeys = newMeta.map((item) => item.key);

      const currentMetaObjects = (loggedInUser.metadata || []).filter(
        (item: KeyValueResp | null) =>
          !newMetaKeys.includes(item?.key) &&
          !metaFieldsToExclude.includes(item?.key as MetadataKey)
      );

      const updatedMeta = [...currentMetaObjects, ...newMeta];

      updateUser({
        id: loggedInUser?.id as string,
        metadata: updatedMeta,
      })
        .then(() => {
          // update user object on update success
          refetchUserData();
          resolve('User updated');
        })
        .catch(() => {
          reject('Something went wrong');
        });
    });
  };

  const updateStepIndexState = (
    nextStepIndex: number,
    options: { forceRender?: boolean } = {}
  ) => {
    const { forceRender = false } = options;
    const maxIndex = Math.max(steps.length - 1, 0);
    const clampedIndex = Math.min(Math.max(nextStepIndex, 0), maxIndex);
    const previousIndex = stepIndexRef.current;

    stepIndexRef.current = clampedIndex;
    setStepIndex(clampedIndex);
    setStepIndexState(clampedIndex);

    if (forceRender && previousIndex === clampedIndex) {
      setTourRenderKey((key) => key + 1);
    }

    // Clear the processing guard — the tooltip event doesn't always fire
    // when stepIndex changes without a full remount, leaving the guard stuck.
    isProcessingStepChangeRef.current = false;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const maxIndex = Math.max(steps.length - 1, 0);
    if (stepIndexRef.current > maxIndex) {
      updateStepIndexState(maxIndex, { forceRender: true });
    }
  }, [steps.length]);

  // ─── Target override helpers ───────────────────────────────────────────────
  // When a step's DOM target isn't found, we temporarily override it to 'body'
  // so Joyride can still render a centered tooltip instead of erroring out.

  const applyTargetOverride = (stepIndex: number) => {
    setTargetOverrides((previous) =>
      previous[stepIndex] === 'body'
        ? previous
        : { ...previous, [stepIndex]: 'body' }
    );
    updateStepIndexState(stepIndex, { forceRender: true });
  };

  const clearTargetOverride = (stepIndex: number) => {
    setTargetOverrides((previous) => {
      if (!previous[stepIndex]) {
        return previous;
      }

      const nextOverrides = { ...previous };
      delete nextOverrides[stepIndex];
      return nextOverrides;
    });
  };

  // ─── Resolve step target ───────────────────────────────────────────────────

  const resolveBodyTarget = (desiredStepIndex: number) => {
    updateStepIndexState(desiredStepIndex, {
      forceRender: desiredStepIndex === stepIndexRef.current,
    });
  };

  const resolveElementTarget = (
    desiredStepIndex: number,
    target: HTMLElement
  ) => {
    if (isElementVisible(target)) {
      clearTargetOverride(desiredStepIndex);
      updateStepIndexState(desiredStepIndex, { forceRender: true });
      return true;
    }
    return false;
  };

  const resolveSelectorTargetWithPolling = (
    desiredStepIndex: number,
    selector: string
  ) => {
    // Cancel any existing polling before starting new
    cancelPollingRef.current?.();

    cancelPollingRef.current = pollUntil(
      () => {
        const element = document.querySelector(selector);
        return element instanceof HTMLElement && isElementVisible(element);
      },
      () => {
        clearTargetOverride(desiredStepIndex);
        updateStepIndexState(desiredStepIndex, { forceRender: true });
        cancelPollingRef.current = null;
      },
      () => {
        // Timeout: fall back to body-centered tooltip for selector targets
        applyTargetOverride(desiredStepIndex);
        cancelPollingRef.current = null;
      }
    );
  };

  const updateStepIndexWhenTargetReady = (
    desiredStepIndex: number,
    fallbackStepIndex?: number,
    options: { allowImmediateFallback?: boolean } = {}
  ) => {
    const hasValidFallback =
      fallbackStepIndex !== undefined &&
      fallbackStepIndex >= 0 &&
      fallbackStepIndex < steps.length;

    if (desiredStepIndex < 0 || desiredStepIndex >= steps.length) {
      if (hasValidFallback && fallbackStepIndex !== undefined) {
        updateStepIndexState(fallbackStepIndex);
      }
      return;
    }

    const target = steps[desiredStepIndex]?.target;
    const { allowImmediateFallback = false } = options;

    // Body targets are always available
    if (target === 'body') {
      resolveBodyTarget(desiredStepIndex);
      return;
    }

    // Direct HTMLElement reference
    if (target instanceof HTMLElement) {
      if (resolveElementTarget(desiredStepIndex, target)) {
        return;
      }
      // Element exists but is hidden — poll for it to become visible
      cancelPollingRef.current?.();
      cancelPollingRef.current = pollUntil(
        () => isElementVisible(target),
        () => {
          clearTargetOverride(desiredStepIndex);
          updateStepIndexState(desiredStepIndex, { forceRender: true });
          cancelPollingRef.current = null;
        },
        () => {
          applyTargetOverride(desiredStepIndex);
          cancelPollingRef.current = null;
        }
      );
      return;
    }

    // CSS selector string
    if (typeof target === 'string') {
      // If immediate fallback is requested and the target isn't ready, show centered
      if (allowImmediateFallback) {
        const immediateTarget = document.querySelector(target);
        if (
          !(immediateTarget instanceof HTMLElement) ||
          !isElementVisible(immediateTarget)
        ) {
          applyTargetOverride(desiredStepIndex);
        }
      }

      resolveSelectorTargetWithPolling(desiredStepIndex, target);
      return;
    }

    // Unknown target type — just move to the step
    resolveBodyTarget(desiredStepIndex);
  };

  /**
   *  Is passed the CallBackProps from the previous step for event type = 'step:after'
   *  Returns a Promise that resolves when any async operations (like tab switching) are complete.
   */
  const dispatchStepBackAction = async ({
    index,
    size,
    step,
  }: CallBackProps) => {
    const phase = getTourStepPhase(step);

    if (phase === TourStepPhase.TalentAcquisition) {
      const hasDeliverablesTourStep = steps.some(
        (s) => getTourStepPhase(s) === TourStepPhase.CustomData
      );
      if (hasDeliverablesTourStep) {
        setIsSideBarCompany(false);
        setIsSideBarOpen(true);
      } else {
        await switchTab(overtimeIndex);
        setIsSideBarOpen(false);
      }

      navigate(`/${PagePaths.COMPANY_COMPOSITIONS}`);
    }

    if (phase === TourStepPhase.CustomData) {
      await switchTab(overtimeIndex);
      setIsSideBarOpen(false);
    }

    if (phase === TourStepPhase.Filters) {
      navigate(`/${PagePaths.COMPANY_COMPOSITIONS}`);
    }

    if (phase === TourStepPhase.AdjustView) {
      await switchTab(snapshotIndex);
    }

    if (phase === TourStepPhase.Compositions) {
      if (!isNewDashboardLayout) {
        navigate(`/`);
        setIsSideBarCompany(true);
        setIsSideBarOpen(true);
      }
    }

    if (phase === TourStepPhase.PageTabs) {
      deferSidebarCloseUntilPageTabsStepRef.current = false;
      navigate(`/`);
      setIsSideBarCompany(true);
      setIsSideBarOpen(true);
    }

    if (phase === TourStepPhase.AnalyticsSubnav) {
      setIsSideBarOpen(false);
      setIsControlPanelSpotlighted(true);
    }

    if (phase === TourStepPhase.ControlPanel) {
      setIsControlPanelSpotlighted(false);
      setIsSideBarOpen(false);
    }

    if (index === size) {
      setIsSideBarCompany(false);
      setIsSideBarOpen(true);
    }
  };

  /**
   *  Is passed the CallBackProps from the previous step for event type = 'step:after'
   */
  const dispatchStepForwardAction = async ({ index, step }: CallBackProps) => {
    const phase = getTourStepPhase(step);

    if (index === 0) {
      setIsControlPanelSpotlighted(true);
    }

    if (phase === TourStepPhase.ControlPanel) {
      setIsControlPanelSpotlighted(false);
      setIsSideBarOpen(true);
    }

    if (phase === TourStepPhase.AnalyticsSubnav) {
      if (isNewDashboardLayout) {
        deferSidebarCloseUntilPageTabsStepRef.current = true;
      } else {
        setIsSideBarOpen(false);
      }
      navigate(`/${PagePaths.COMPANY_COMPOSITIONS}`);
    }

    if (phase === TourStepPhase.Filters) {
      await switchTab(overtimeIndex);
    }

    const hasCustomDataStep = steps.some(
      (s) => getTourStepPhase(s) === TourStepPhase.CustomData
    );
    if (phase === TourStepPhase.AdjustView && hasCustomDataStep) {
      setIsSideBarCompany(false);
      setIsSideBarOpen(true);
    }

    const hasTalentDiscoveryStep = steps.some(
      (s) => getTourStepPhase(s) === TourStepPhase.TalentAcquisition
    );
    if (
      (phase === TourStepPhase.AdjustView &&
        step.showProgress === true &&
        hasTalentDiscoveryStep &&
        !hasCustomDataStep) ||
      (phase === TourStepPhase.CustomData &&
        step.showProgress === true &&
        hasTalentDiscoveryStep)
    ) {
      setIsSideBarCompany(false);
      setIsSideBarOpen(false);
      navigate(`/${PagePaths.TALENT_DISCOVERY}`);
    }
  };

  const dispatchJoyrideAction = async (
    data: CallBackProps,
    isStepBack: boolean
  ) => {
    if (isStepBack) {
      await dispatchStepBackAction(data);
      return;
    }

    await dispatchStepForwardAction(data);
  };

  const completeTour = (skipped: boolean, index: number) => {
    deferSidebarCloseUntilPageTabsStepRef.current = false;
    setIsTour(false);
    setIsControlPanelSpotlighted(false);
    setIsSideBarOpen(false);
    setIsSideBarCompany(true);
    updateStepIndexState(0);

    setIsTourComplete(true);

    handleUpdateMetadata(loggedInUser, [
      {
        key: MetadataKey.TourCompleted,
        value: 'true',
      },
    ]);

    const mixpanelEvent = skipped
      ? InitiationTrackingEvents.SKIP_TOUR
      : InitiationTrackingEvents.FINISH_TOUR;

    const mixpanelProps = skipped ? { step_index: index } : {};

    mixpanel.track(mixpanelEvent, mixpanelProps);

    navigate(`${PagePaths.LANDING}`);
  };

  /**
   * Joyride's callback prop expects a synchronous function, but our step
   * transitions involve async work (e.g. tab switching). We wrap the async
   * logic in a void IIFE so errors are caught rather than silently swallowed
   * by the Promise that an async callback would return (which Joyride ignores).
   */

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, action, index } = data;

      const nextStepEvents = [
        EVENTS.STEP_AFTER,
        EVENTS.TARGET_NOT_FOUND,
      ] as string[];

      if (type === EVENTS.TOUR_END && status === STATUS.SKIPPED) {
        completeTour(true, index);
        return;
      }

      // Joyride fires a 'tooltip' event (not in its public typings) when
      // the tooltip finishes rendering. We use this to know the step
      // transition is complete and we can accept new user actions.
      if (type === ('tooltip' as unknown as typeof type)) {
        isProcessingStepChangeRef.current = false;
      }

      if (nextStepEvents.includes(type)) {
        // Guard against spurious Joyride callbacks during programmatic step
        // transitions (see isProcessingStepChangeRef docs above)
        if (type === EVENTS.STEP_AFTER && isProcessingStepChangeRef.current) {
          return;
        }

        // Mark that we're processing a step change
        isProcessingStepChangeRef.current = true;

        const isStepBack = action === ACTIONS.PREV;
        const nextStepIndex = index + (isStepBack ? -1 : 1);

        if (type === EVENTS.STEP_AFTER) {
          if (action === ACTIONS.NEXT && index === steps.length - 1) {
            completeTour(false, index);
            return;
          }
        }

        void (async () => {
          try {
            await dispatchJoyrideAction(data, isStepBack);

            if (type === EVENTS.TARGET_NOT_FOUND) {
              updateStepIndexWhenTargetReady(index, nextStepIndex, {
                allowImmediateFallback: true,
              });
              return;
            }

            updateStepIndexWhenTargetReady(nextStepIndex);
          } catch (error) {
            console.error('Tour step transition failed:', error);
          }
        })();
      }
    },
    [steps, loggedInUser, isNewDashboardLayout]
  );

  const stepsWithOverrides = useMemo(
    () =>
      steps.map((step, index) => {
        const overrideTarget = targetOverrides[index];
        if (!overrideTarget) {
          return step;
        }
        return { ...step, target: overrideTarget };
      }),
    [steps, targetOverrides]
  );

  if (!isTourState) {
    return null;
  }

  return (
    <Joyride
      key={tourRenderKey}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      steps={stepsWithOverrides}
      run={isTourState}
      styles={tourStyles}
      stepIndex={stepIndexState}
      floaterProps={{
        disableAnimation: true,
      }}
      locale={{
        next: stepIndexState === 0 ? 'Start Tour' : 'Next',
        skip: 'Skip Tour',
        last: 'Finish ',
      }}
      // Stop user from leaving tour when in progress
      disableOverlayClose
      disableCloseOnEsc
      hideCloseButton
    />
  );
}

export default Tour;
